import React from "react";
import { ChatDocumentAnswerMeta } from "@shared/api/chatApi";

/**
 * 검증된 발주서 사실 답변의 부가 렌더(plan §Task 23) — 품질 배지, 라인 페이징, 되묻기 후보.
 * 모든 문자열은 React 텍스트로만 렌더한다(마크다운/HTML 미해석 — 적대 문자열 불활성).
 * 후보 선택은 questionId+candidateId 전송일 뿐 로컬 사실 데이터를 절대 수정하지 않으며,
 * ERP 필드 매핑·적용/저장 동작은 존재하지 않는다.
 */

/** 품질 배지 — COMPLETE와 REVIEW_REQUIRED를 시각적으로 구분한다. */
export function DocumentQualityBadge({ meta }: { meta: ChatDocumentAnswerMeta | undefined }) {
  if (!meta) {
    return null;
  }
  const complete = meta.documentQuality === "COMPLETE";
  return (
    <span
      className={`chatbot-document-quality ${
        complete ? "chatbot-document-quality-complete" : "chatbot-document-quality-review"
      }`}
      data-testid="document-quality-badge"
    >
      {complete ? "추출 완전" : "검토 필요"}
    </span>
  );
}

export interface DocumentCandidateOption {
  label: string;
  candidateId: string;
}

export interface DocumentAnswerExtrasProps {
  meta?: ChatDocumentAnswerMeta;
  /** DOCUMENT_CLARIFICATION 되묻기 — 서버 후보 목록(라벨은 literal 텍스트로만). */
  candidates?: DocumentCandidateOption[];
  questionId?: string;
  attachmentId?: string;
  busy: boolean;
  onCandidate: (attachmentId: string, questionId: string, candidateId: string) => void;
  onPage: (attachmentId: string, offset: number) => void;
}

export function DocumentAnswerExtras({
  meta,
  candidates,
  questionId,
  attachmentId,
  busy,
  onCandidate,
  onPage,
}: DocumentAnswerExtrasProps) {
  const showPaging =
    meta !== undefined
    && meta.operation === "LINE_LIST"
    && meta.hasMore
    && meta.nextOffset !== undefined
    && meta.nextOffset !== null
    && attachmentId !== undefined;
  const showCandidates =
    candidates !== undefined
    && candidates.length > 0
    && questionId !== undefined
    && attachmentId !== undefined;
  if (!showPaging && !showCandidates) {
    return null;
  }
  return (
    <div className="chatbot-document-extras">
      {showCandidates && (
        <div className="chatbot-selection-options" data-testid="document-candidates">
          {candidates.map((candidate) => (
            <button
              className="chatbot-selection-option"
              disabled={busy}
              key={candidate.candidateId}
              onClick={() => onCandidate(attachmentId, questionId, candidate.candidateId)}
              type="button"
            >
              {candidate.label}
            </button>
          ))}
        </div>
      )}
      {showPaging && meta && (
        <button
          className="chatbot-document-page-more"
          data-testid="document-page-more"
          disabled={busy}
          onClick={() => onPage(attachmentId, meta.nextOffset as number)}
          type="button"
        >
          다음 100행 보기 ({(meta.nextOffset as number) + 1}-
          {Math.min((meta.nextOffset as number) + 100, meta.total)} / 총 {meta.total}행)
        </button>
      )}
    </div>
  );
}
